(() => {
(function() {
    const initializeSlider = (slider)=>{
        if (slider.dataset.initialized === 'true') return;
        const track = slider.querySelector('.bmtg-hero-slider__track');
        const slides = Array.from(slider.querySelectorAll('.bmtg-hero-slide'));
        const previous = slider.querySelector('[data-slider-previous]');
        const next = slider.querySelector('[data-slider-next]');
        const dots = Array.from(slider.querySelectorAll('[data-slider-dot]'));
        const status = slider.querySelector('[data-slider-status]');
        const rotation = slider.querySelector('[data-slider-rotation]');
        const loop = slider.dataset.loop !== 'false';
        const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        let reducedMotion = motionQuery.matches;
        let activeIndex = 0;
        let frame = 0;
        let programmaticIndex = null;
        let settleTimer = 0;
        let prefetchEnabled = false;
        if (!track || slides.length < 1) return;
        const statusTemplate = status?.dataset.statusTemplate || '%1$d / %2$d';
        const announce = (index)=>{
            if (status) status.textContent = statusTemplate.replace('%1$d', String(index + 1)).replace('%2$d', String(slides.length));
        };
        const markDot = (dot, isCurrent)=>{
            dot.classList.toggle('is-active', isCurrent);
            if (isCurrent) dot.setAttribute('aria-current', 'true');
            else dot.removeAttribute('aria-current');
        };
        const clampIndex = (index)=>{
            if (loop) return (index + slides.length) % slides.length;
            return Math.max(0, Math.min(index, slides.length - 1));
        };
        // Every slide but the first ships with loading="lazy". Flipping the whole
        // set to eager pulls the entire gallery over the wire even for visitors who
        // never advance the slider, so only the slides that are one step away get
        // prepared — dropping "lazy" starts the fetch while the slide is still off
        // screen and lets the browser re-evaluate the <picture> sources.
        const prepareSlide = (index, priority)=>{
            const slide = slides[clampIndex(index)];
            const image = slide ? slide.querySelector('img') : null;
            if (!image || 'lazy' !== image.loading) return;
            if ('fetchPriority' in image) image.fetchPriority = priority || 'low';
            image.loading = 'eager';
        };
        // Both neighbours: arrows, dots and a wrapping autoplay all move backwards
        // as well as forwards.
        const prepareNeighbours = (index)=>{
            if (!prefetchEnabled) return;
            prepareSlide(index + 1);
            prepareSlide(index - 1);
        };
        const setActive = (index)=>{
            activeIndex = clampIndex(index);
            slides.forEach((slide, slideIndex)=>{
                const isActive = slideIndex === activeIndex;
                slide.classList.toggle('is-active', isActive);
                slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
                slide.inert = !isActive;
            });
            dots.forEach((dot, dotIndex)=>markDot(dot, dotIndex === activeIndex));
            if (previous && !loop) previous.disabled = activeIndex === 0;
            if (next && !loop) next.disabled = activeIndex === slides.length - 1;
            announce(activeIndex);
            prepareNeighbours(activeIndex);
        };
        const setDots = (index)=>{
            const current = clampIndex(index);
            dots.forEach((dot, dotIndex)=>markDot(dot, dotIndex === current));
            announce(current);
        };
        // Sync ARIA/inert state to wherever the track actually is. This is the
        // single source of truth: the visible slide must never be aria-hidden.
        const settle = ()=>{
            programmaticIndex = null;
            updateFromScroll();
        };
        const goTo = (index, moveFocus, instant)=>{
            const targetIndex = clampIndex(index);
            const target = slides[targetIndex];
            if (!target) return;
            const left = targetIndex * track.clientWidth;
            prepareSlide(targetIndex, 'high');
            programmaticIndex = targetIndex;
            setDots(targetIndex);
            window.clearTimeout(settleTimer);
            if (Math.abs(track.scrollLeft - left) < 1) settle();
            else {
                track.scrollTo({
                    left: left,
                    behavior: instant || reducedMotion ? 'auto' : 'smooth'
                });
                settleTimer = window.setTimeout(settle, instant || reducedMotion ? 80 : 400);
            }
            if (moveFocus) track.focus({
                preventScroll: true
            });
        };
        const updateFromScroll = ()=>{
            frame = 0;
            const trackLeft = track.getBoundingClientRect().left;
            if (null !== programmaticIndex) {
                const requestedSlide = slides[programmaticIndex];
                const requestedDistance = Math.abs(requestedSlide.getBoundingClientRect().left - trackLeft);
                if (requestedDistance > 1) return;
                programmaticIndex = null;
            }
            let closestIndex = 0;
            let closestDistance = Number.POSITIVE_INFINITY;
            slides.forEach((slide, index)=>{
                const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = index;
                }
            });
            if (closestIndex !== activeIndex) setActive(closestIndex);
        };
        previous?.addEventListener('click', ()=>{
            stopAutoplay();
            goTo((programmaticIndex ?? activeIndex) - 1, false);
        });
        next?.addEventListener('click', ()=>{
            stopAutoplay();
            goTo((programmaticIndex ?? activeIndex) + 1, false);
        });
        dots.forEach((dot, index)=>dot.addEventListener('click', ()=>{
                stopAutoplay();
                goTo(index, false);
            }));
        track.addEventListener('pointerdown', ()=>{
            programmaticIndex = null;
            stopAutoplay();
        }, {
            passive: true
        });
        track.addEventListener('wheel', ()=>{
            programmaticIndex = null;
            stopAutoplay();
        }, {
            passive: true
        });
        track.addEventListener('keydown', (event)=>{
            if ('ArrowLeft' === event.key) {
                event.preventDefault();
                stopAutoplay();
                goTo((programmaticIndex ?? activeIndex) - 1, false, true);
            }
            if ('ArrowRight' === event.key) {
                event.preventDefault();
                stopAutoplay();
                goTo((programmaticIndex ?? activeIndex) + 1, false, true);
            }
        });
        const AUTOPLAY_INTERVAL = 6000;
        let autoplayTimer = 0;
        let pointerRequested = null;
        let rotationRequested = slider.dataset.autoplay === 'true' && !reducedMotion;
        let hovering = slider.matches(':hover');
        const syncRotation = ()=>{
            if (rotation) {
                rotation.hidden = false;
                rotation.textContent = rotationRequested ? rotation.dataset.stopLabel : rotation.dataset.startLabel;
            }
            status?.setAttribute('aria-live', autoplayTimer ? 'off' : 'polite');
        };
        const autoplayAllowed = ()=>rotationRequested && 1 < slides.length;
        const stopAutoplay = (preserveRequest = false)=>{
            if (preserveRequest !== true) rotationRequested = false;
            window.clearInterval(autoplayTimer);
            autoplayTimer = 0;
            syncRotation();
        };
        const startAutoplay = ()=>{
            if (!autoplayAllowed() || autoplayTimer || document.hidden || hovering) return;
            autoplayTimer = window.setInterval(()=>{
                if (!loop && activeIndex === slides.length - 1) {
                    stopAutoplay();
                    return;
                }
                goTo(activeIndex + 1, false);
            }, AUTOPLAY_INTERVAL);
            syncRotation();
        };
        if (typeof motionQuery.addEventListener === 'function') motionQuery.addEventListener('change', (event)=>{
            reducedMotion = event.matches;
            if (reducedMotion) stopAutoplay();
            else startAutoplay();
        });
        // Pause while the visitor is reading or operating the slider.
        slider.addEventListener('pointerenter', ()=>{
            hovering = true;
            stopAutoplay(true);
        });
        slider.addEventListener('pointerleave', ()=>{
            hovering = false;
            startAutoplay();
        });
        slider.addEventListener('focusin', stopAutoplay);
        rotation?.addEventListener('pointerdown', ()=>{
            pointerRequested = rotationRequested;
        });
        rotation?.addEventListener('pointercancel', ()=>{
            pointerRequested = null;
        });
        rotation?.addEventListener('click', (event)=>{
            const shouldStop = event.detail > 0 && pointerRequested !== null ? pointerRequested : rotationRequested;
            pointerRequested = null;
            if (shouldStop) stopAutoplay();
            else {
                rotationRequested = true;
                hovering = false;
                startAutoplay();
            }
            syncRotation();
        });
        document.addEventListener('visibilitychange', ()=>{
            if (document.hidden) stopAutoplay(true);
            else startAutoplay();
        });
        // Warm the reachable slides once the page has settled, so the first
        // navigation does not pop in an empty slide. Held back until 'load' plus an
        // idle slot so the prefetch never competes with the LCP hero image; every
        // slide further out stays lazy until the visitor walks up to it.
        const enablePrefetch = ()=>{
            prefetchEnabled = true;
            prepareNeighbours(activeIndex);
        };
        const schedulePrefetch = ()=>{
            if (window.requestIdleCallback) window.requestIdleCallback(enablePrefetch, {
                timeout: 2000
            });
            else window.setTimeout(enablePrefetch, 1500);
        };
        if ('complete' === document.readyState) schedulePrefetch();
        else window.addEventListener('load', schedulePrefetch, {
            once: true
        });
        track.addEventListener('scroll', ()=>{
            if (!frame) frame = window.requestAnimationFrame(updateFromScroll);
        }, {
            passive: true
        });
        slider.dataset.initialized = 'true';
        slider.classList.add('is-enhanced');
        setActive(0);
        syncRotation();
        startAutoplay();
    };
    const initializeAll = ()=>{
        document.querySelectorAll('[data-bmtg-hero-slider]').forEach(initializeSlider);
    };
    if ('loading' === document.readyState) document.addEventListener('DOMContentLoaded', initializeAll, {
        once: true
    });
    else initializeAll();
})();

})();
//# sourceMappingURL=hero-slider.js.map
